/**
 * US Bilateral Income Tax Treaties - Independent Personal Services & Business Profits
 *
 * Comprehensive dataset of treaty provisions relevant to contractor/freelancer income.
 * Based on IRS Publication 901 (Rev. September 2024), IRS Tax Treaty Tables, and
 * individual treaty texts.
 *
 * Sources:
 *   - IRS Publication 901: https://www.irs.gov/publications/p901
 *   - IRS Tax Treaty Tables: https://www.irs.gov/individuals/international-taxpayers/tax-treaty-tables
 *   - IRS Treaties A-Z: https://www.irs.gov/businesses/international-businesses/united-states-income-tax-treaties-a-to-z
 *
 * IMPORTANT:
 *   - Hungary treaty TERMINATED effective January 1, 2024
 *   - Russia treaty SUSPENDED (Articles 5-21, 23) effective August 16, 2024
 *   - Belarus treaty partially suspended by Belarus (dividends, interest, property gains),
 *     but independent personal services provisions remain in effect
 *   - Chile treaty is NEW, effective January 1, 2024
 *
 * Last updated: 2026-03-18
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export enum TreatyStatus {
  Active = "active",
  Terminated = "terminated",
  Suspended = "suspended",
  PartiallySuspended = "partially_suspended"
}

export enum TreatyArticleType {
  BusinessProfits = "business_profits",
  IndependentPersonalServices = "independent_personal_services",
  OtherIncome = "other_income"
}

export interface TreatyProvision {
  /** The article number as it appears in the treaty text (e.g., "7", "14", "VII", "VI(2)") */
  articleNumber: string;
  /** Human-readable article title */
  articleTitle: string;
  /** The type of article */
  articleType: TreatyArticleType;
  /** Withholding rate in percent. 0 = exempt (most common for contractors). */
  rate: number;
  /** Conditions that must be met for the exemption/reduced rate to apply */
  conditions: string;
  /**
   * Maximum days the individual may be present in the US during the relevant
   * period while still qualifying for the exemption. null = no day limit
   * (exemption based solely on fixed base / PE).
   */
  maxDaysPresent: number | null;
  /** The period over which maxDaysPresent is measured */
  dayCountPeriod: "tax_year" | "12_month_rolling" | "calendar_year" | null;
  /**
   * Maximum compensation that can be earned while still qualifying for
   * exemption. null = no compensation limit.
   */
  maxCompensation: number | null;
  /** Currency of maxCompensation (always USD for US treaties) */
  maxCompensationCurrency: "USD" | null;
  /** Whether a "no fixed base" or "no permanent establishment" condition applies */
  requiresNoFixedBase: boolean;
  /** Whether this is typically the most advantageous provision for contractors */
  mostAdvantageous: boolean;
}

export interface TreatyCountry {
  /** ISO 3166-1 alpha-2 country code */
  countryCode: string;
  /** Full country name */
  countryName: string;
  /** Current status of the treaty */
  status: TreatyStatus;
  /** Notes about treaty status (e.g., termination date, suspension details) */
  statusNote: string | null;
  /**
   * Whether this country's treaty covers independent personal services via
   * Article 7 (Business Profits) rather than a separate Article 14.
   * Newer treaties tend to fold independent services into business profits.
   */
  usesBusinessProfitsForContractors: boolean;
  /** Array of applicable treaty provisions for contractor/freelancer income */
  provisions: TreatyProvision[];
  /** The IRS form used to claim this treaty benefit for independent services */
  claimForm: "8233" | "W-8BEN";
  /**
   * Name of the underlying treaty if this country inherits from a
   * predecessor treaty (e.g., "US-USSR" for CIS countries).
   */
  inheritedTreaty: string | null;
}

// ---------------------------------------------------------------------------
// Helper to build provision objects with sensible defaults
// ---------------------------------------------------------------------------

function businessProfitsProvision(
  overrides: Partial<TreatyProvision> & { articleNumber: string }
): TreatyProvision {
  return {
    articleTitle: "Business Profits",
    articleType: TreatyArticleType.BusinessProfits,
    rate: 0,
    conditions:
      "Business profits exempt from US tax unless individual has a permanent establishment (PE) in the US. If PE exists, only profits attributable to the PE are taxable.",
    maxDaysPresent: null,
    dayCountPeriod: null,
    maxCompensation: null,
    maxCompensationCurrency: null,
    requiresNoFixedBase: true,
    mostAdvantageous: true,
    ...overrides
  };
}

function independentServicesProvision(
  overrides: Partial<TreatyProvision> & { articleNumber: string }
): TreatyProvision {
  return {
    articleTitle: "Independent Personal Services",
    articleType: TreatyArticleType.IndependentPersonalServices,
    rate: 0,
    conditions:
      "Exempt from US tax if: (1) no fixed base regularly available in the US, AND (2) present in the US for no more than 183 days during the tax year.",
    maxDaysPresent: 183,
    dayCountPeriod: "tax_year",
    maxCompensation: null,
    maxCompensationCurrency: null,
    requiresNoFixedBase: true,
    mostAdvantageous: true,
    ...overrides
  };
}

// ---------------------------------------------------------------------------
// Complete Treaty Dataset
// ---------------------------------------------------------------------------

export const US_TAX_TREATY_COUNTRIES: TreatyCountry[] = [
  // =========================================================================
  // ARMENIA (inherits US-USSR treaty)
  // =========================================================================
  {
    countryCode: "AM",
    countryName: "Armenia",
    status: TreatyStatus.Active,
    statusNote: null,
    usesBusinessProfitsForContractors: false,
    claimForm: "8233",
    inheritedTreaty: "US-USSR",
    provisions: [
      independentServicesProvision({
        articleNumber: "VI(2)",
        conditions:
          "Exempt from US tax if present in the US for no more than 183 days during the tax year. Income must be for services performed for or on behalf of a resident of Armenia.",
        maxDaysPresent: 183,
        dayCountPeriod: "tax_year"
      })
    ]
  },

  // =========================================================================
  // AUSTRALIA
  // =========================================================================
  {
    countryCode: "AU",
    countryName: "Australia",
    status: TreatyStatus.Active,
    statusNote: null,
    usesBusinessProfitsForContractors: false,
    claimForm: "8233",
    inheritedTreaty: null,
    provisions: [
      independentServicesProvision({
        articleNumber: "14",
        conditions:
          "Exempt from US tax if: (1) present in the US for no more than 183 days during any 12-month period beginning or ending in the tax year, AND (2) no fixed base regularly available in the US for performing the services.",
        maxDaysPresent: 183,
        dayCountPeriod: "12_month_rolling"
      })
    ]
  },

  // =========================================================================
  // AUSTRIA
  // =========================================================================
  {
    countryCode: "AT",
    countryName: "Austria",
    status: TreatyStatus.Active,
    statusNote: null,
    usesBusinessProfitsForContractors: false,
    claimForm: "8233",
    inheritedTreaty: null,
    provisions: [
      independentServicesProvision({
        articleNumber: "14",
        conditions:
          "Exempt from US tax if no fixed base regularly available in the US for performing the services. No day limit applies - exemption is based solely on absence of a fixed base.",
        maxDaysPresent: null,
        dayCountPeriod: null
      })
    ]
  },

  // =========================================================================
  // AZERBAIJAN (inherits US-USSR treaty)
  // =========================================================================
  {
    countryCode: "AZ",
    countryName: "Azerbaijan",
    status: TreatyStatus.Active,
    statusNote: null,
    usesBusinessProfitsForContractors: false,
    claimForm: "8233",
    inheritedTreaty: "US-USSR",
    provisions: [
      independentServicesProvision({
        articleNumber: "VI(2)",
        conditions:
          "Exempt from US tax if present in the US for no more than 183 days during the tax year. Income must be for services performed for or on behalf of a resident of Azerbaijan.",
        maxDaysPresent: 183,
        dayCountPeriod: "tax_year"
      })
    ]
  },

  // =========================================================================
  // BANGLADESH
  // =========================================================================
  {
    countryCode: "BD",
    countryName: "Bangladesh",
    status: TreatyStatus.Active,
    statusNote: null,
    usesBusinessProfitsForContractors: false,
    claimForm: "8233",
    inheritedTreaty: null,
    provisions: [
      independentServicesProvision({
        articleNumber: "15",
        conditions:
          "Exempt from US tax if: (1) present in the US for no more than 183 days during the tax year, AND (2) no fixed base regularly available in the US for performing the services.",
        maxDaysPresent: 183,
        dayCountPeriod: "tax_year"
      })
    ]
  },

  // =========================================================================
  // BARBADOS
  // =========================================================================
  {
    countryCode: "BB",
    countryName: "Barbados",
    status: TreatyStatus.Active,
    statusNote: null,
    usesBusinessProfitsForContractors: false,
    claimForm: "8233",
    inheritedTreaty: null,
    provisions: [
      independentServicesProvision({
        articleNumber: "14",
        conditions:
          "Exempt from US tax if: (1) present in the US for no more than 89 days during the tax year, AND (2) compensation does not exceed $5,000 for the tax year, AND (3) services are performed for or on behalf of a US resident employer.",
        maxDaysPresent: 89,
        dayCountPeriod: "tax_year",
        maxCompensation: 5000,
        maxCompensationCurrency: "USD"
      })
    ]
  },

  // =========================================================================
  // BELARUS (inherits US-USSR treaty, partially suspended)
  // =========================================================================
  {
    countryCode: "BY",
    countryName: "Belarus",
    status: TreatyStatus.PartiallySuspended,
    statusNote:
      "Belarus suspended provisions related to dividends, interest, and property gains effective December 17, 2024. Independent personal services provisions remain in effect.",
    usesBusinessProfitsForContractors: false,
    claimForm: "8233",
    inheritedTreaty: "US-USSR",
    provisions: [
      independentServicesProvision({
        articleNumber: "VI(2)",
        conditions:
          "Exempt from US tax if present in the US for no more than 183 days during the tax year. Income must be for services performed for or on behalf of a resident of Belarus. Note: This provision remains in effect despite partial treaty suspension.",
        maxDaysPresent: 183,
        dayCountPeriod: "tax_year"
      })
    ]
  },

  // =========================================================================
  // BELGIUM
  // =========================================================================
  {
    countryCode: "BE",
    countryName: "Belgium",
    status: TreatyStatus.Active,
    statusNote: null,
    usesBusinessProfitsForContractors: true,
    claimForm: "8233",
    inheritedTreaty: null,
    provisions: [
      businessProfitsProvision({
        articleNumber: "7",
        conditions:
          "Business profits exempt from US tax unless individual has a permanent establishment (PE) in the US. No separate independent personal services article - all contractor income falls under business profits. If PE exists, only profits attributable to the PE are taxable."
      })
    ]
  },

  // =========================================================================
  // BULGARIA
  // =========================================================================
  {
    countryCode: "BG",
    countryName: "Bulgaria",
    status: TreatyStatus.Active,
    statusNote: null,
    usesBusinessProfitsForContractors: true,
    claimForm: "8233",
    inheritedTreaty: null,
    provisions: [
      businessProfitsProvision({
        articleNumber: "7",
        conditions:
          "Business profits exempt from US tax unless individual has a permanent establishment (PE) in the US. No separate independent personal services article. If PE exists, only profits attributable to the PE are taxable."
      })
    ]
  },

  // =========================================================================
  // CANADA
  // =========================================================================
  {
    countryCode: "CA",
    countryName: "Canada",
    status: TreatyStatus.Active,
    statusNote: null,
    usesBusinessProfitsForContractors: true,
    claimForm: "8233",
    inheritedTreaty: null,
    provisions: [
      businessProfitsProvision({
        articleNumber: "VII",
        conditions:
          "Business profits exempt from US tax unless individual has a permanent establishment (PE) in the US. Canada treaty uses Roman numeral article numbering. Includes a service-based PE concept: providing services in the US for more than 183 days in any 12-month period may create a PE even without a fixed place of business."
      })
    ]
  },

  // =========================================================================
  // CHILE (NEW - effective January 1, 2024)
  // =========================================================================
  {
    countryCode: "CL",
    countryName: "Chile",
    status: TreatyStatus.Active,
    statusNote: "New treaty effective January 1, 2024.",
    usesBusinessProfitsForContractors: false,
    claimForm: "8233",
    inheritedTreaty: null,
    provisions: [
      independentServicesProvision({
        articleNumber: "14",
        conditions:
          "Exempt from US tax if: (1) present in the US for less than 183 days during any 12-month period beginning or ending in the tax year, AND (2) no fixed base regularly available in the US for performing the services.",
        maxDaysPresent: 182,
        dayCountPeriod: "12_month_rolling"
      })
    ]
  },

  // =========================================================================
  // CHINA (People's Republic)
  // =========================================================================
  {
    countryCode: "CN",
    countryName: "China",
    status: TreatyStatus.Active,
    statusNote:
      "Treaty applies to mainland China only. Does not apply to Hong Kong SAR or Taiwan.",
    usesBusinessProfitsForContractors: false,
    claimForm: "8233",
    inheritedTreaty: null,
    provisions: [
      independentServicesProvision({
        articleNumber: "13",
        conditions:
          "Exempt from US tax if: (1) present in the US for no more than 183 days during the tax year, AND (2) no fixed base regularly available in the US for performing the services.",
        maxDaysPresent: 183,
        dayCountPeriod: "tax_year"
      })
    ]
  },

  // =========================================================================
  // CYPRUS
  // =========================================================================
  {
    countryCode: "CY",
    countryName: "Cyprus",
    status: TreatyStatus.Active,
    statusNote: null,
    usesBusinessProfitsForContractors: false,
    claimForm: "8233",
    inheritedTreaty: null,
    provisions: [
      independentServicesProvision({
        articleNumber: "17",
        conditions:
          "Exempt from US tax if: (1) present in the US for no more than 182 days during the tax year, AND (2) no fixed base regularly available in the US for performing the services.",
        maxDaysPresent: 182,
        dayCountPeriod: "tax_year"
      })
    ]
  },

  // =========================================================================
  // CZECH REPUBLIC
  // =========================================================================
  {
    countryCode: "CZ",
    countryName: "Czech Republic",
    status: TreatyStatus.Active,
    statusNote: null,
    usesBusinessProfitsForContractors: false,
    claimForm: "8233",
    inheritedTreaty: null,
    provisions: [
      independentServicesProvision({
        articleNumber: "14",
        conditions:
          "Exempt from US tax if: (1) present in the US for no more than 183 days in any 12-month period beginning or ending in the tax year, AND (2) no fixed base regularly available in the US for performing the services.",
        maxDaysPresent: 183,
        dayCountPeriod: "12_month_rolling"
      })
    ]
  },

  // =========================================================================
  // DENMARK
  // =========================================================================
  {
    countryCode: "DK",
    countryName: "Denmark",
    status: TreatyStatus.Active,
    statusNote: null,
    usesBusinessProfitsForContractors: false,
    claimForm: "8233",
    inheritedTreaty: null,
    provisions: [
      independentServicesProvision({
        articleNumber: "14",
        conditions:
          "Exempt from US tax if no fixed base regularly available in the US for performing the services. No day limit applies - exemption is based solely on absence of a fixed base.",
        maxDaysPresent: null,
        dayCountPeriod: null
      })
    ]
  },

  // =========================================================================
  // EGYPT
  // =========================================================================
  {
    countryCode: "EG",
    countryName: "Egypt",
    status: TreatyStatus.Active,
    statusNote: null,
    usesBusinessProfitsForContractors: false,
    claimForm: "8233",
    inheritedTreaty: null,
    provisions: [
      independentServicesProvision({
        articleNumber: "15",
        conditions:
          "Exempt from US tax if present in the US for no more than 89 days during the tax year and no fixed base regularly available in the US.",
        maxDaysPresent: 89,
        dayCountPeriod: "tax_year"
      })
    ]
  },

  // =========================================================================
  // ESTONIA
  // =========================================================================
  {
    countryCode: "EE",
    countryName: "Estonia",
    status: TreatyStatus.Active,
    statusNote: null,
    usesBusinessProfitsForContractors: false,
    claimForm: "8233",
    inheritedTreaty: null,
    provisions: [
      independentServicesProvision({
        articleNumber: "14",
        conditions:
          "Exempt from US tax if: (1) present in the US for no more than 183 days in any 12-month period beginning or ending in the tax year, AND (2) no fixed base regularly available in the US for performing the services.",
        maxDaysPresent: 183,
        dayCountPeriod: "12_month_rolling"
      })
    ]
  },

  // =========================================================================
  // FINLAND
  // =========================================================================
  {
    countryCode: "FI",
    countryName: "Finland",
    status: TreatyStatus.Active,
    statusNote: null,
    usesBusinessProfitsForContractors: false,
    claimForm: "8233",
    inheritedTreaty: null,
    provisions: [
      independentServicesProvision({
        articleNumber: "14",
        conditions:
          "Exempt from US tax if no fixed base regularly available in the US for performing the services. No day limit applies - exemption is based solely on absence of a fixed base.",
        maxDaysPresent: null,
        dayCountPeriod: null
      })
    ]
  },

  // =========================================================================
  // FRANCE
  // =========================================================================
  {
    countryCode: "FR",
    countryName: "France",
    status: TreatyStatus.Active,
    statusNote: null,
    usesBusinessProfitsForContractors: false,
    claimForm: "8233",
    inheritedTreaty: null,
    provisions: [
      independentServicesProvision({
        articleNumber: "14",
        conditions:
          "Exempt from US tax if no fixed base regularly available in the US for performing the services. No day limit applies - exemption is based solely on absence of a fixed base.",
        maxDaysPresent: null,
        dayCountPeriod: null
      })
    ]
  },

  // =========================================================================
  // GEORGIA (inherits US-USSR treaty)
  // =========================================================================
  {
    countryCode: "GE",
    countryName: "Georgia",
    status: TreatyStatus.Active,
    statusNote: null,
    usesBusinessProfitsForContractors: false,
    claimForm: "8233",
    inheritedTreaty: "US-USSR",
    provisions: [
      independentServicesProvision({
        articleNumber: "VI(2)",
        conditions:
          "Exempt from US tax if present in the US for no more than 183 days during the tax year. Income must be for services performed for or on behalf of a resident of Georgia.",
        maxDaysPresent: 183,
        dayCountPeriod: "tax_year"
      })
    ]
  },

  // =========================================================================
  // GERMANY
  // =========================================================================
  {
    countryCode: "DE",
    countryName: "Germany",
    status: TreatyStatus.Active,
    statusNote: null,
    usesBusinessProfitsForContractors: true,
    claimForm: "8233",
    inheritedTreaty: null,
    provisions: [
      businessProfitsProvision({
        articleNumber: "7",
        conditions:
          "Business profits exempt from US tax unless individual has a permanent establishment (PE) in the US. No separate independent personal services article. If PE exists, only profits attributable to the PE are taxable."
      })
    ]
  },

  // =========================================================================
  // GREECE
  // =========================================================================
  {
    countryCode: "GR",
    countryName: "Greece",
    status: TreatyStatus.Active,
    statusNote:
      "Older treaty with Roman numeral article numbering and more restrictive conditions.",
    usesBusinessProfitsForContractors: false,
    claimForm: "8233",
    inheritedTreaty: null,
    provisions: [
      independentServicesProvision({
        articleNumber: "X",
        conditions:
          "Exempt from US tax if: (1) present in the US for no more than 183 days during the tax year, AND (2) compensation does not exceed $10,000 for the tax year.",
        maxDaysPresent: 183,
        dayCountPeriod: "tax_year",
        maxCompensation: 10000,
        maxCompensationCurrency: "USD"
      })
    ]
  },

  // =========================================================================
  // HUNGARY (TERMINATED)
  // =========================================================================
  {
    countryCode: "HU",
    countryName: "Hungary",
    status: TreatyStatus.Terminated,
    statusNote:
      "Treaty terminated by the US effective January 1, 2024. No treaty benefits available for tax years beginning on or after January 1, 2024. Standard 30% withholding applies.",
    usesBusinessProfitsForContractors: false,
    claimForm: "8233",
    inheritedTreaty: null,
    provisions: []
  },

  // =========================================================================
  // ICELAND
  // =========================================================================
  {
    countryCode: "IS",
    countryName: "Iceland",
    status: TreatyStatus.Active,
    statusNote: null,
    usesBusinessProfitsForContractors: true,
    claimForm: "8233",
    inheritedTreaty: null,
    provisions: [
      businessProfitsProvision({
        articleNumber: "7",
        conditions:
          "Business profits exempt from US tax unless individual has a permanent establishment (PE) in the US. No separate independent personal services article. If PE exists, only profits attributable to the PE are taxable."
      })
    ]
  },

  // =========================================================================
  // INDIA
  // =========================================================================
  {
    countryCode: "IN",
    countryName: "India",
    status: TreatyStatus.Active,
    statusNote: null,
    usesBusinessProfitsForContractors: false,
    claimForm: "8233",
    inheritedTreaty: null,
    provisions: [
      independentServicesProvision({
        articleNumber: "15",
        conditions:
          "Exempt from US tax if: (1) present in the US for no more than 89 days during the tax year, AND (2) no fixed base regularly available in the US for performing the services.",
        maxDaysPresent: 89,
        dayCountPeriod: "tax_year"
      })
    ]
  },

  // =========================================================================
  // INDONESIA
  // =========================================================================
  {
    countryCode: "ID",
    countryName: "Indonesia",
    status: TreatyStatus.Active,
    statusNote: null,
    usesBusinessProfitsForContractors: false,
    claimForm: "8233",
    inheritedTreaty: null,
    provisions: [
      independentServicesProvision({
        articleNumber: "15",
        conditions:
          "Exempt from US tax if: (1) present in the US for no more than 119 days during any 12-month period, AND (2) no fixed base regularly available in the US for performing the services.",
        maxDaysPresent: 119,
        dayCountPeriod: "12_month_rolling"
      })
    ]
  },

  // =========================================================================
  // IRELAND
  // =========================================================================
  {
    countryCode: "IE",
    countryName: "Ireland",
    status: TreatyStatus.Active,
    statusNote: null,
    usesBusinessProfitsForContractors: false,
    claimForm: "8233",
    inheritedTreaty: null,
    provisions: [
      independentServicesProvision({
        articleNumber: "14",
        conditions:
          "Exempt from US tax if no fixed base regularly available in the US for performing the services. No day limit applies - exemption is based solely on absence of a fixed base.",
        maxDaysPresent: null,
        dayCountPeriod: null
      })
    ]
  },

  // =========================================================================
  // ISRAEL
  // =========================================================================
  {
    countryCode: "IL",
    countryName: "Israel",
    status: TreatyStatus.Active,
    statusNote: null,
    usesBusinessProfitsForContractors: false,
    claimForm: "8233",
    inheritedTreaty: null,
    provisions: [
      independentServicesProvision({
        articleNumber: "16",
        conditions:
          "Exempt from US tax if: (1) present in the US for no more than 182 days during the tax year, AND (2) no fixed base regularly available in the US for performing the services.",
        maxDaysPresent: 182,
        dayCountPeriod: "tax_year"
      })
    ]
  },

  // =========================================================================
  // ITALY
  // =========================================================================
  {
    countryCode: "IT",
    countryName: "Italy",
    status: TreatyStatus.Active,
    statusNote: null,
    usesBusinessProfitsForContractors: false,
    claimForm: "8233",
    inheritedTreaty: null,
    provisions: [
      independentServicesProvision({
        articleNumber: "14(1)",
        conditions:
          "Exempt from US tax if no fixed base regularly available in the US for performing the services. No day limit applies - exemption is based solely on absence of a fixed base.",
        maxDaysPresent: null,
        dayCountPeriod: null
      })
    ]
  },

  // =========================================================================
  // JAMAICA
  // =========================================================================
  {
    countryCode: "JM",
    countryName: "Jamaica",
    status: TreatyStatus.Active,
    statusNote: null,
    usesBusinessProfitsForContractors: false,
    claimForm: "8233",
    inheritedTreaty: null,
    provisions: [
      independentServicesProvision({
        articleNumber: "14",
        conditions:
          "Exempt from US tax if: (1) present in the US for no more than 89 days during the tax year, AND (2) compensation does not exceed $5,000 for the tax year, AND (3) services are performed for or on behalf of a US resident employer.",
        maxDaysPresent: 89,
        dayCountPeriod: "tax_year",
        maxCompensation: 5000,
        maxCompensationCurrency: "USD"
      })
    ]
  },

  // =========================================================================
  // JAPAN
  // =========================================================================
  {
    countryCode: "JP",
    countryName: "Japan",
    status: TreatyStatus.Active,
    statusNote: null,
    usesBusinessProfitsForContractors: true,
    claimForm: "8233",
    inheritedTreaty: null,
    provisions: [
      businessProfitsProvision({
        articleNumber: "7",
        conditions:
          "Business profits exempt from US tax unless individual has a permanent establishment (PE) in the US. No separate independent personal services article. If PE exists, only profits attributable to the PE are taxable."
      })
    ]
  },

  // =========================================================================
  // KAZAKHSTAN
  // =========================================================================
  {
    countryCode: "KZ",
    countryName: "Kazakhstan",
    status: TreatyStatus.Active,
    statusNote: null,
    usesBusinessProfitsForContractors: false,
    claimForm: "8233",
    inheritedTreaty: null,
    provisions: [
      independentServicesProvision({
        articleNumber: "14",
        conditions:
          "Exempt from US tax if: (1) present in the US for no more than 183 days in any 12-month period beginning or ending in the tax year, AND (2) no fixed base regularly available in the US for performing the services.",
        maxDaysPresent: 183,
        dayCountPeriod: "12_month_rolling"
      })
    ]
  },

  // =========================================================================
  // KOREA, SOUTH
  // =========================================================================
  {
    countryCode: "KR",
    countryName: "Korea, South",
    status: TreatyStatus.Active,
    statusNote: null,
    usesBusinessProfitsForContractors: false,
    claimForm: "8233",
    inheritedTreaty: null,
    provisions: [
      independentServicesProvision({
        articleNumber: "18",
        conditions:
          "Exempt from US tax if: (1) present in the US for no more than 182 days during the tax year, AND (2) compensation does not exceed $3,000 for the tax year.",
        maxDaysPresent: 182,
        dayCountPeriod: "tax_year",
        maxCompensation: 3000,
        maxCompensationCurrency: "USD"
      })
    ]
  },

  // =========================================================================
  // KYRGYZSTAN (inherits US-USSR treaty)
  // =========================================================================
  {
    countryCode: "KG",
    countryName: "Kyrgyzstan",
    status: TreatyStatus.Active,
    statusNote: null,
    usesBusinessProfitsForContractors: false,
    claimForm: "8233",
    inheritedTreaty: "US-USSR",
    provisions: [
      independentServicesProvision({
        articleNumber: "VI(2)",
        conditions:
          "Exempt from US tax if present in the US for no more than 183 days during the tax year. Income must be for services performed for or on behalf of a resident of Kyrgyzstan.",
        maxDaysPresent: 183,
        dayCountPeriod: "tax_year"
      })
    ]
  },

  // =========================================================================
  // LATVIA
  // =========================================================================
  {
    countryCode: "LV",
    countryName: "Latvia",
    status: TreatyStatus.Active,
    statusNote: null,
    usesBusinessProfitsForContractors: false,
    claimForm: "8233",
    inheritedTreaty: null,
    provisions: [
      independentServicesProvision({
        articleNumber: "14",
        conditions:
          "Exempt from US tax if: (1) present in the US for no more than 183 days in any 12-month period beginning or ending in the tax year, AND (2) no fixed base regularly available in the US for performing the services.",
        maxDaysPresent: 183,
        dayCountPeriod: "12_month_rolling"
      })
    ]
  },

  // =========================================================================
  // LITHUANIA
  // =========================================================================
  {
    countryCode: "LT",
    countryName: "Lithuania",
    status: TreatyStatus.Active,
    statusNote: null,
    usesBusinessProfitsForContractors: false,
    claimForm: "8233",
    inheritedTreaty: null,
    provisions: [
      independentServicesProvision({
        articleNumber: "14",
        conditions:
          "Exempt from US tax if: (1) present in the US for no more than 183 days in any 12-month period beginning or ending in the tax year, AND (2) no fixed base regularly available in the US for performing the services.",
        maxDaysPresent: 183,
        dayCountPeriod: "12_month_rolling"
      })
    ]
  },

  // =========================================================================
  // LUXEMBOURG
  // =========================================================================
  {
    countryCode: "LU",
    countryName: "Luxembourg",
    status: TreatyStatus.Active,
    statusNote: null,
    usesBusinessProfitsForContractors: false,
    claimForm: "8233",
    inheritedTreaty: null,
    provisions: [
      independentServicesProvision({
        articleNumber: "15",
        conditions:
          "Exempt from US tax if no fixed base regularly available in the US for performing the services. No day limit applies - exemption is based solely on absence of a fixed base.",
        maxDaysPresent: null,
        dayCountPeriod: null
      })
    ]
  },

  // =========================================================================
  // MALTA
  // =========================================================================
  {
    countryCode: "MT",
    countryName: "Malta",
    status: TreatyStatus.Active,
    statusNote: null,
    usesBusinessProfitsForContractors: true,
    claimForm: "8233",
    inheritedTreaty: null,
    provisions: [
      businessProfitsProvision({
        articleNumber: "7",
        conditions:
          "Business profits exempt from US tax unless individual has a permanent establishment (PE) in the US. No separate independent personal services article. If PE exists, only profits attributable to the PE are taxable."
      })
    ]
  },

  // =========================================================================
  // MEXICO
  // =========================================================================
  {
    countryCode: "MX",
    countryName: "Mexico",
    status: TreatyStatus.Active,
    statusNote: null,
    usesBusinessProfitsForContractors: false,
    claimForm: "8233",
    inheritedTreaty: null,
    provisions: [
      independentServicesProvision({
        articleNumber: "14",
        conditions:
          "Exempt from US tax if: (1) present in the US for no more than 183 days during any 12-month period beginning or ending in the tax year, AND (2) no fixed base regularly available in the US for performing the services.",
        maxDaysPresent: 183,
        dayCountPeriod: "12_month_rolling"
      })
    ]
  },

  // =========================================================================
  // MOLDOVA (inherits US-USSR treaty)
  // =========================================================================
  {
    countryCode: "MD",
    countryName: "Moldova",
    status: TreatyStatus.Active,
    statusNote: null,
    usesBusinessProfitsForContractors: false,
    claimForm: "8233",
    inheritedTreaty: "US-USSR",
    provisions: [
      independentServicesProvision({
        articleNumber: "VI(2)",
        conditions:
          "Exempt from US tax if present in the US for no more than 183 days during the tax year. Income must be for services performed for or on behalf of a resident of Moldova.",
        maxDaysPresent: 183,
        dayCountPeriod: "tax_year"
      })
    ]
  },

  // =========================================================================
  // MOROCCO
  // =========================================================================
  {
    countryCode: "MA",
    countryName: "Morocco",
    status: TreatyStatus.Active,
    statusNote: null,
    usesBusinessProfitsForContractors: false,
    claimForm: "8233",
    inheritedTreaty: null,
    provisions: [
      independentServicesProvision({
        articleNumber: "14",
        conditions:
          "Exempt from US tax if: (1) present in the US for no more than 182 days during the tax year, AND (2) compensation does not exceed $5,000 for the tax year, AND (3) no fixed base regularly available in the US.",
        maxDaysPresent: 182,
        dayCountPeriod: "tax_year",
        maxCompensation: 5000,
        maxCompensationCurrency: "USD"
      })
    ]
  },

  // =========================================================================
  // NETHERLANDS
  // =========================================================================
  {
    countryCode: "NL",
    countryName: "Netherlands",
    status: TreatyStatus.Active,
    statusNote: null,
    usesBusinessProfitsForContractors: false,
    claimForm: "8233",
    inheritedTreaty: null,
    provisions: [
      independentServicesProvision({
        articleNumber: "15",
        conditions:
          "Exempt from US tax if no fixed base regularly available in the US for performing the services. No day limit applies - exemption is based solely on absence of a fixed base.",
        maxDaysPresent: null,
        dayCountPeriod: null
      })
    ]
  },

  // =========================================================================
  // NEW ZEALAND
  // =========================================================================
  {
    countryCode: "NZ",
    countryName: "New Zealand",
    status: TreatyStatus.Active,
    statusNote: null,
    usesBusinessProfitsForContractors: false,
    claimForm: "8233",
    inheritedTreaty: null,
    provisions: [
      independentServicesProvision({
        articleNumber: "14",
        conditions:
          "Exempt from US tax if: (1) present in the US for no more than 183 days during the tax year, AND (2) no fixed base regularly available in the US for performing the services.",
        maxDaysPresent: 183,
        dayCountPeriod: "tax_year"
      })
    ]
  },

  // =========================================================================
  // NORWAY
  // =========================================================================
  {
    countryCode: "NO",
    countryName: "Norway",
    status: TreatyStatus.Active,
    statusNote: null,
    usesBusinessProfitsForContractors: false,
    claimForm: "8233",
    inheritedTreaty: null,
    provisions: [
      independentServicesProvision({
        articleNumber: "13",
        conditions:
          "Exempt from US tax if: (1) present in the US for no more than 182 days during the tax year, AND (2) no fixed base regularly available in the US for performing the services.",
        maxDaysPresent: 182,
        dayCountPeriod: "tax_year"
      })
    ]
  },

  // =========================================================================
  // PAKISTAN
  // =========================================================================
  {
    countryCode: "PK",
    countryName: "Pakistan",
    status: TreatyStatus.Active,
    statusNote: "Older treaty with Roman numeral article numbering.",
    usesBusinessProfitsForContractors: false,
    claimForm: "8233",
    inheritedTreaty: null,
    provisions: [
      independentServicesProvision({
        articleNumber: "XI",
        conditions:
          "Exempt from US tax if: (1) present in the US for no more than 183 days during the tax year, AND (2) services are performed for or on behalf of a resident of Pakistan, AND (3) individual is subject to Pakistani tax on the income.",
        maxDaysPresent: 183,
        dayCountPeriod: "tax_year"
      })
    ]
  },

  // =========================================================================
  // PHILIPPINES
  // =========================================================================
  {
    countryCode: "PH",
    countryName: "Philippines",
    status: TreatyStatus.Active,
    statusNote: null,
    usesBusinessProfitsForContractors: false,
    claimForm: "8233",
    inheritedTreaty: null,
    provisions: [
      independentServicesProvision({
        articleNumber: "15",
        conditions:
          "Exempt from US tax if: (1) present in the US for no more than 89 days during the tax year, AND (2) compensation does not exceed $10,000 for the tax year, AND (3) services performed for or on behalf of a US resident employer.",
        maxDaysPresent: 89,
        dayCountPeriod: "tax_year",
        maxCompensation: 10000,
        maxCompensationCurrency: "USD"
      })
    ]
  },

  // =========================================================================
  // POLAND
  // =========================================================================
  {
    countryCode: "PL",
    countryName: "Poland",
    status: TreatyStatus.Active,
    statusNote: null,
    usesBusinessProfitsForContractors: false,
    claimForm: "8233",
    inheritedTreaty: null,
    provisions: [
      independentServicesProvision({
        articleNumber: "15",
        conditions:
          "Exempt from US tax if: (1) present in the US for no more than 182 days during the tax year, AND (2) no fixed base regularly available in the US for performing the services.",
        maxDaysPresent: 182,
        dayCountPeriod: "tax_year"
      })
    ]
  },

  // =========================================================================
  // PORTUGAL
  // =========================================================================
  {
    countryCode: "PT",
    countryName: "Portugal",
    status: TreatyStatus.Active,
    statusNote: null,
    usesBusinessProfitsForContractors: false,
    claimForm: "8233",
    inheritedTreaty: null,
    provisions: [
      independentServicesProvision({
        articleNumber: "15",
        conditions:
          "Exempt from US tax if: (1) present in the US for no more than 182 days during the tax year, AND (2) no fixed base regularly available in the US for performing the services.",
        maxDaysPresent: 182,
        dayCountPeriod: "tax_year"
      })
    ]
  },

  // =========================================================================
  // ROMANIA
  // =========================================================================
  {
    countryCode: "RO",
    countryName: "Romania",
    status: TreatyStatus.Active,
    statusNote: null,
    usesBusinessProfitsForContractors: false,
    claimForm: "8233",
    inheritedTreaty: null,
    provisions: [
      independentServicesProvision({
        articleNumber: "14",
        conditions:
          "Exempt from US tax if: (1) present in the US for no more than 182 days during the tax year, AND (2) no fixed base regularly available in the US for performing the services.",
        maxDaysPresent: 182,
        dayCountPeriod: "tax_year"
      })
    ]
  },

  // =========================================================================
  // RUSSIA (SUSPENDED)
  // =========================================================================
  {
    countryCode: "RU",
    countryName: "Russia",
    status: TreatyStatus.Suspended,
    statusNote:
      "Articles 5-21 and 23 of the US-Russia treaty suspended by the US effective August 16, 2024. Independent personal services provisions (Article 13) are NOT available. Standard 30% withholding applies.",
    usesBusinessProfitsForContractors: false,
    claimForm: "8233",
    inheritedTreaty: null,
    provisions: []
  },

  // =========================================================================
  // SLOVAK REPUBLIC
  // =========================================================================
  {
    countryCode: "SK",
    countryName: "Slovak Republic",
    status: TreatyStatus.Active,
    statusNote: null,
    usesBusinessProfitsForContractors: false,
    claimForm: "8233",
    inheritedTreaty: null,
    provisions: [
      independentServicesProvision({
        articleNumber: "14",
        conditions:
          "Exempt from US tax if: (1) present in the US for no more than 183 days in any 12-month period beginning or ending in the tax year, AND (2) no fixed base regularly available in the US for performing the services.",
        maxDaysPresent: 183,
        dayCountPeriod: "12_month_rolling"
      })
    ]
  },

  // =========================================================================
  // SLOVENIA
  // =========================================================================
  {
    countryCode: "SI",
    countryName: "Slovenia",
    status: TreatyStatus.Active,
    statusNote: null,
    usesBusinessProfitsForContractors: false,
    claimForm: "8233",
    inheritedTreaty: null,
    provisions: [
      independentServicesProvision({
        articleNumber: "14",
        conditions:
          "Exempt from US tax if no fixed base regularly available in the US for performing the services. No day limit applies - exemption is based solely on absence of a fixed base.",
        maxDaysPresent: null,
        dayCountPeriod: null
      })
    ]
  },

  // =========================================================================
  // SOUTH AFRICA
  // =========================================================================
  {
    countryCode: "ZA",
    countryName: "South Africa",
    status: TreatyStatus.Active,
    statusNote: null,
    usesBusinessProfitsForContractors: false,
    claimForm: "8233",
    inheritedTreaty: null,
    provisions: [
      independentServicesProvision({
        articleNumber: "14",
        conditions:
          "Exempt from US tax if: (1) present in the US for no more than 183 days during the tax year, AND (2) no fixed base regularly available in the US for performing the services.",
        maxDaysPresent: 183,
        dayCountPeriod: "tax_year"
      })
    ]
  },

  // =========================================================================
  // SPAIN
  // =========================================================================
  {
    countryCode: "ES",
    countryName: "Spain",
    status: TreatyStatus.Active,
    statusNote: null,
    usesBusinessProfitsForContractors: false,
    claimForm: "8233",
    inheritedTreaty: null,
    provisions: [
      independentServicesProvision({
        articleNumber: "15",
        conditions:
          "Exempt from US tax if no fixed base regularly available in the US for performing the services. No day limit applies - exemption is based solely on absence of a fixed base.",
        maxDaysPresent: null,
        dayCountPeriod: null
      })
    ]
  },

  // =========================================================================
  // SRI LANKA
  // =========================================================================
  {
    countryCode: "LK",
    countryName: "Sri Lanka",
    status: TreatyStatus.Active,
    statusNote: null,
    usesBusinessProfitsForContractors: false,
    claimForm: "8233",
    inheritedTreaty: null,
    provisions: [
      independentServicesProvision({
        articleNumber: "15",
        conditions:
          "Exempt from US tax if: (1) present in the US for no more than 183 days during the tax year, AND (2) no fixed base regularly available in the US for performing the services.",
        maxDaysPresent: 183,
        dayCountPeriod: "tax_year"
      })
    ]
  },

  // =========================================================================
  // SWEDEN
  // =========================================================================
  {
    countryCode: "SE",
    countryName: "Sweden",
    status: TreatyStatus.Active,
    statusNote: null,
    usesBusinessProfitsForContractors: false,
    claimForm: "8233",
    inheritedTreaty: null,
    provisions: [
      independentServicesProvision({
        articleNumber: "14",
        conditions:
          "Exempt from US tax if no fixed base regularly available in the US for performing the services. No day limit applies - exemption is based solely on absence of a fixed base.",
        maxDaysPresent: null,
        dayCountPeriod: null
      })
    ]
  },

  // =========================================================================
  // SWITZERLAND
  // =========================================================================
  {
    countryCode: "CH",
    countryName: "Switzerland",
    status: TreatyStatus.Active,
    statusNote: null,
    usesBusinessProfitsForContractors: false,
    claimForm: "8233",
    inheritedTreaty: null,
    provisions: [
      independentServicesProvision({
        articleNumber: "14",
        conditions:
          "Exempt from US tax if no fixed base regularly available in the US for performing the services. No day limit applies - exemption is based solely on absence of a fixed base.",
        maxDaysPresent: null,
        dayCountPeriod: null
      })
    ]
  },

  // =========================================================================
  // TAJIKISTAN (inherits US-USSR treaty)
  // =========================================================================
  {
    countryCode: "TJ",
    countryName: "Tajikistan",
    status: TreatyStatus.Active,
    statusNote: null,
    usesBusinessProfitsForContractors: false,
    claimForm: "8233",
    inheritedTreaty: "US-USSR",
    provisions: [
      independentServicesProvision({
        articleNumber: "VI(2)",
        conditions:
          "Exempt from US tax if present in the US for no more than 183 days during the tax year. Income must be for services performed for or on behalf of a resident of Tajikistan.",
        maxDaysPresent: 183,
        dayCountPeriod: "tax_year"
      })
    ]
  },

  // =========================================================================
  // THAILAND
  // =========================================================================
  {
    countryCode: "TH",
    countryName: "Thailand",
    status: TreatyStatus.Active,
    statusNote: null,
    usesBusinessProfitsForContractors: false,
    claimForm: "8233",
    inheritedTreaty: null,
    provisions: [
      independentServicesProvision({
        articleNumber: "15",
        conditions:
          "Exempt from US tax if: (1) present in the US for no more than 89 days during the tax year, AND (2) no fixed base regularly available in the US. For foreign contractors (not working for a US employer): no compensation limit. For US-employer contractors: compensation limit of $10,000.",
        maxDaysPresent: 89,
        dayCountPeriod: "tax_year",
        maxCompensation: 10000,
        maxCompensationCurrency: "USD"
      })
    ]
  },

  // =========================================================================
  // TRINIDAD AND TOBAGO
  // =========================================================================
  {
    countryCode: "TT",
    countryName: "Trinidad and Tobago",
    status: TreatyStatus.Active,
    statusNote: null,
    usesBusinessProfitsForContractors: false,
    claimForm: "8233",
    inheritedTreaty: null,
    provisions: [
      independentServicesProvision({
        articleNumber: "17",
        conditions:
          "Exempt from US tax if: (1) present in the US for no more than 183 days during the tax year, AND (2) no fixed base regularly available in the US. For US-employer contractors: compensation limit of $3,000. For foreign-employer contractors: no compensation limit.",
        maxDaysPresent: 183,
        dayCountPeriod: "tax_year",
        maxCompensation: 3000,
        maxCompensationCurrency: "USD"
      })
    ]
  },

  // =========================================================================
  // TUNISIA
  // =========================================================================
  {
    countryCode: "TN",
    countryName: "Tunisia",
    status: TreatyStatus.Active,
    statusNote: null,
    usesBusinessProfitsForContractors: false,
    claimForm: "8233",
    inheritedTreaty: null,
    provisions: [
      independentServicesProvision({
        articleNumber: "14",
        conditions:
          "Exempt from US tax if: (1) present in the US for no more than 183 days during the tax year, AND (2) compensation does not exceed $7,500 for the tax year, AND (3) services performed for or on behalf of a US resident employer.",
        maxDaysPresent: 183,
        dayCountPeriod: "tax_year",
        maxCompensation: 7500,
        maxCompensationCurrency: "USD"
      })
    ]
  },

  // =========================================================================
  // TURKEY
  // =========================================================================
  {
    countryCode: "TR",
    countryName: "Turkey",
    status: TreatyStatus.Active,
    statusNote: null,
    usesBusinessProfitsForContractors: false,
    claimForm: "8233",
    inheritedTreaty: null,
    provisions: [
      independentServicesProvision({
        articleNumber: "14",
        conditions:
          "Exempt from US tax if: (1) present in the US for no more than 183 days during the tax year, AND (2) no fixed base regularly available in the US for performing the services.",
        maxDaysPresent: 183,
        dayCountPeriod: "tax_year"
      })
    ]
  },

  // =========================================================================
  // TURKMENISTAN (inherits US-USSR treaty)
  // =========================================================================
  {
    countryCode: "TM",
    countryName: "Turkmenistan",
    status: TreatyStatus.Active,
    statusNote: null,
    usesBusinessProfitsForContractors: false,
    claimForm: "8233",
    inheritedTreaty: "US-USSR",
    provisions: [
      independentServicesProvision({
        articleNumber: "VI(2)",
        conditions:
          "Exempt from US tax if present in the US for no more than 183 days during the tax year. Income must be for services performed for or on behalf of a resident of Turkmenistan.",
        maxDaysPresent: 183,
        dayCountPeriod: "tax_year"
      })
    ]
  },

  // =========================================================================
  // UKRAINE
  // =========================================================================
  {
    countryCode: "UA",
    countryName: "Ukraine",
    status: TreatyStatus.Active,
    statusNote: null,
    usesBusinessProfitsForContractors: false,
    claimForm: "8233",
    inheritedTreaty: null,
    provisions: [
      independentServicesProvision({
        articleNumber: "14",
        conditions:
          "Exempt from US tax if no fixed base regularly available in the US for performing the services. No day limit applies - exemption is based solely on absence of a fixed base.",
        maxDaysPresent: null,
        dayCountPeriod: null
      })
    ]
  },

  // =========================================================================
  // UNITED KINGDOM
  // =========================================================================
  {
    countryCode: "GB",
    countryName: "United Kingdom",
    status: TreatyStatus.Active,
    statusNote:
      "The 2001 treaty with 2002 Protocol eliminated Article 14 (Independent Personal Services). Contractor income is governed solely by Article 7 (Business Profits).",
    usesBusinessProfitsForContractors: true,
    claimForm: "8233",
    inheritedTreaty: null,
    provisions: [
      businessProfitsProvision({
        articleNumber: "7",
        conditions:
          "Business profits exempt from US tax unless individual has a permanent establishment (PE) in the US. The 2002 Protocol specifically eliminated Article 14, folding independent personal services into business profits. If PE exists, only profits attributable to the PE are taxable."
      })
    ]
  },

  // =========================================================================
  // UZBEKISTAN (inherits US-USSR treaty)
  // =========================================================================
  {
    countryCode: "UZ",
    countryName: "Uzbekistan",
    status: TreatyStatus.Active,
    statusNote: null,
    usesBusinessProfitsForContractors: false,
    claimForm: "8233",
    inheritedTreaty: "US-USSR",
    provisions: [
      independentServicesProvision({
        articleNumber: "VI(2)",
        conditions:
          "Exempt from US tax if present in the US for no more than 183 days during the tax year. Income must be for services performed for or on behalf of a resident of Uzbekistan.",
        maxDaysPresent: 183,
        dayCountPeriod: "tax_year"
      })
    ]
  },

  // =========================================================================
  // VENEZUELA
  // =========================================================================
  {
    countryCode: "VE",
    countryName: "Venezuela",
    status: TreatyStatus.Active,
    statusNote: null,
    usesBusinessProfitsForContractors: false,
    claimForm: "8233",
    inheritedTreaty: null,
    provisions: [
      independentServicesProvision({
        articleNumber: "14",
        conditions:
          "Exempt from US tax if no fixed base regularly available in the US for performing the services. No day limit applies - exemption is based solely on absence of a fixed base.",
        maxDaysPresent: null,
        dayCountPeriod: null
      })
    ]
  }
];

// ---------------------------------------------------------------------------
// Lookup Utilities
// ---------------------------------------------------------------------------

/** Map from ISO country code to treaty data for O(1) lookups */
export const TREATY_BY_COUNTRY_CODE: Record<string, TreatyCountry> =
  US_TAX_TREATY_COUNTRIES.reduce(
    (acc, country) => {
      acc[country.countryCode] = country;
      return acc;
    },
    {} as Record<string, TreatyCountry>
  );

/** Get treaty data by ISO 2-letter country code. Returns undefined if no treaty exists. */
export function getTreatyByCountryCode(
  code: string
): TreatyCountry | undefined {
  return TREATY_BY_COUNTRY_CODE[code.toUpperCase()];
}

/** Get all active treaty countries (excludes terminated and fully suspended) */
export function getActiveTreatyCountries(): TreatyCountry[] {
  return US_TAX_TREATY_COUNTRIES.filter(
    c =>
      c.status === TreatyStatus.Active ||
      c.status === TreatyStatus.PartiallySuspended
  );
}

/** Get all countries that use Article 7 (Business Profits) for contractor income */
export function getBusinessProfitsCountries(): TreatyCountry[] {
  return US_TAX_TREATY_COUNTRIES.filter(
    c => c.usesBusinessProfitsForContractors && c.provisions.length > 0
  );
}

/** Get all countries that have a separate Independent Personal Services article */
export function getIndependentServicesCountries(): TreatyCountry[] {
  return US_TAX_TREATY_COUNTRIES.filter(
    c => !c.usesBusinessProfitsForContractors && c.provisions.length > 0
  );
}

/**
 * Check whether a contractor from a given country is exempt from US withholding
 * based on their days present and compensation earned.
 *
 * Returns { exempt: boolean; reason: string } explaining the determination.
 *
 * IMPORTANT: This is a simplified eligibility check. Actual treaty benefit
 * eligibility requires professional tax advice and consideration of the full
 * treaty text, limitation on benefits provisions, and individual circumstances.
 */
export function checkContractorExemption(
  countryCode: string,
  daysInUS: number,
  compensationUSD: number
): { exempt: boolean; reason: string } {
  const treaty = getTreatyByCountryCode(countryCode);

  if (!treaty) {
    return {
      exempt: false,
      reason: `No US tax treaty exists with country code "${countryCode}". Standard 30% withholding applies.`
    };
  }

  if (
    treaty.status === TreatyStatus.Terminated ||
    treaty.status === TreatyStatus.Suspended
  ) {
    return {
      exempt: false,
      reason: `The US tax treaty with ${treaty.countryName} is ${treaty.status}. ${treaty.statusNote || ""} Standard 30% withholding applies.`
    };
  }

  if (treaty.provisions.length === 0) {
    return {
      exempt: false,
      reason: `The US tax treaty with ${treaty.countryName} does not have active provisions for contractor income. Standard 30% withholding applies.`
    };
  }

  // Check the most advantageous provision
  const provision =
    treaty.provisions.find(p => p.mostAdvantageous) || treaty.provisions[0];

  // Check day limit
  if (
    provision.maxDaysPresent !== null &&
    daysInUS > provision.maxDaysPresent
  ) {
    return {
      exempt: false,
      reason: `Contractor exceeded the ${provision.maxDaysPresent}-day presence limit under ${treaty.countryName} treaty Article ${provision.articleNumber}. Present for ${daysInUS} days. Standard 30% withholding applies.`
    };
  }

  // Check compensation limit
  if (
    provision.maxCompensation !== null &&
    compensationUSD > provision.maxCompensation
  ) {
    return {
      exempt: false,
      reason: `Contractor compensation ($${compensationUSD.toLocaleString()}) exceeds the $${provision.maxCompensation.toLocaleString()} limit under ${treaty.countryName} treaty Article ${provision.articleNumber}. Standard 30% withholding applies.`
    };
  }

  return {
    exempt: true,
    reason: `Contractor qualifies for exemption under ${treaty.countryName} treaty Article ${provision.articleNumber} (${provision.articleTitle}). ${provision.conditions}`
  };
}

// ---------------------------------------------------------------------------
// Summary Constants
// ---------------------------------------------------------------------------

/** Total number of US tax treaty countries in the dataset */
export const TOTAL_TREATY_COUNTRIES = US_TAX_TREATY_COUNTRIES.length;

/** Countries where the treaty uses Article 7 (Business Profits) for contractors */
export const ARTICLE_7_COUNTRIES = [
  "BE",
  "BG",
  "CA",
  "DE",
  "IS",
  "JP",
  "MT",
  "GB"
] as const;

/** Countries that inherit the US-USSR treaty */
export const USSR_TREATY_COUNTRIES = [
  "AM",
  "AZ",
  "BY",
  "GE",
  "KG",
  "MD",
  "TJ",
  "TM",
  "UZ"
] as const;

/** Countries with NO day limit (exempt based solely on no fixed base / no PE) */
export const NO_DAY_LIMIT_COUNTRIES = [
  "AT",
  "BE",
  "BG",
  "CA",
  "DE",
  "DK",
  "ES",
  "FI",
  "FR",
  "GB",
  "IE",
  "IS",
  "IT",
  "JP",
  "LU",
  "MT",
  "NL",
  "SE",
  "CH",
  "SI",
  "UA",
  "VE"
] as const;

/** Countries with compensation limits on the exemption */
export const COMPENSATION_LIMIT_COUNTRIES: Record<string, number> = {
  BB: 5000, // Barbados
  GR: 10000, // Greece
  JM: 5000, // Jamaica
  KR: 3000, // Korea, South
  MA: 5000, // Morocco
  PH: 10000, // Philippines
  TH: 10000, // Thailand
  TN: 7500, // Tunisia
  TT: 3000 // Trinidad and Tobago
};

/** Countries with a short (< 183 day) presence limit */
export const SHORT_PRESENCE_LIMIT_COUNTRIES: Record<string, number> = {
  BB: 89, // Barbados
  EG: 89, // Egypt
  IN: 89, // India
  ID: 119, // Indonesia
  JM: 89, // Jamaica
  PH: 89, // Philippines
  TH: 89 // Thailand
};
